import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';

const AuthContext = createContext();

const getReadableError = (err, fallbackMessage) => {
  const serverMessage = err?.response?.data?.error?.message || err?.response?.data?.message;
  if (serverMessage) return serverMessage;

  if (err?.message === 'Network Error') {
    return '無法連線到伺服器，請稍後再試';
  }

  if (err?.response?.status) {
    return `${fallbackMessage} (HTTP ${err.response.status})`;
  }

  return err?.message || fallbackMessage;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCurrentUser = async (jwtToken) => {
    const response = await axiosInstance.get('/auth/me', {
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    const currentUser = response.data.user;
    setUser(currentUser);
    return currentUser;
  };

  const setAuthSession = async (jwtToken, fallbackUser = null) => {
    localStorage.setItem('token', jwtToken);
    setToken(jwtToken);

    try {
      return await fetchCurrentUser(jwtToken);
    } catch (err) {
      if (fallbackUser) {
        setUser(fallbackUser);
        return fallbackUser;
      }
      throw err;
    }
  };

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        setToken(storedToken);
        try {
          await fetchCurrentUser(storedToken);
        } catch (err) {
          console.error('Failed to fetch user:', err);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const register = async (email, password, firstName, lastName) => {
    setError(null);
    setIsLoading(true);
    try {
      const normalizedEmail = typeof email === 'string' ? email.trim().toLowerCase() : email;
      const response = await axiosInstance.post('/auth/register', {
        email: normalizedEmail,
        password,
        firstName,
        lastName,
      });
      const { jwt, user: userData } = response.data;
      await setAuthSession(jwt, userData);
      return { success: true, user: userData };
    } catch (err) {
      const message = getReadableError(err, 'Registration failed');
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    setError(null);
    setIsLoading(true);
    try {
      const normalizedEmail = typeof email === 'string' ? email.trim().toLowerCase() : email;
      const response = await axiosInstance.post('/auth/login', {
        email: normalizedEmail,
        password,
      });
      const { jwt, user: userData } = response.data;
      await setAuthSession(jwt, userData);
      return { success: true, user: userData };
    } catch (err) {
      const message = getReadableError(err, 'Login failed');
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setError(null);
  };

  const updateProfile = async (profileData, profilePictureFile = null, removeProfilePicture = false) => {
    setError(null);
    setIsLoading(true);
    try {
      const normalizedPayload = {
        ...profileData,
        age:
          profileData?.age === '' || profileData?.age === null || profileData?.age === undefined
            ? null
            : Number(profileData.age),
      };

      let response;
      if (profilePictureFile || removeProfilePicture) {
        const formData = new FormData();
        Object.entries(normalizedPayload).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, String(value));
          }
        });
        if (profilePictureFile) {
          formData.append('profilePicture', profilePictureFile);
        }
        if (removeProfilePicture) {
          formData.append('removeProfilePicture', 'true');
        }
        response = await axiosInstance.put('/auth/profile', formData);
      } else {
        response = await axiosInstance.put('/auth/profile', normalizedPayload);
      }

      const updatedUser = { ...user, ...response.data.user };
      setUser(updatedUser);
      return { success: true, user: updatedUser };
    } catch (err) {
      const message =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        'Update failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    token,
    isLoading,
    error,
    register,
    login,
    logout,
    updateProfile,
    fetchCurrentUser,
    setAuthSession,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
